import React, { PureComponent } from 'react';
import classNames from 'classnames';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  LabelList,
  Tooltip,
  ResponsiveContainer,
  TooltipFormatter,
  LabelFormatter,
} from 'recharts';
import colors from '@spotify-internal/encore-foundation';
import chartStyles from 'styles/charts.module.scss';
import { GenderDemographics } from '../../../../types';
import {
  FormattedMessage,
  FormattedNumber,
  injectIntl,
  IntlShape,
  defineMessage,
} from 'react-intl';
import { formatPercentage } from '../../../../lib/Formatter';

type Props = {
  ageData: GenderDemographics[];
  intl: IntlShape;
};

class InternationalizedAgeDemographicsChart extends PureComponent<Props> {
  tooltipFormatter: TooltipFormatter = /* istanbul ignore next */ (
    _,
    label,
    { fill, dataKey, payload },
  ) => (
    <div>
      <span className={chartStyles['audience-tooltip-circle']} style={{ backgroundColor: fill }} />
      <span className={chartStyles['audience-tooltip-label']}>
        <FormattedMessage
          id={`network.audience.age.tooltip-${label}`}
          description={`Tooltip label ${label}`}
          defaultMessage={label}
        />
      </span>
      <span className={chartStyles['audience-tooltip-value']}>
        <FormattedNumber value={payload[`${dataKey}Percentage`]} style="percent" />
      </span>
    </div>
  );

  tooltipLabelFormatter: LabelFormatter = /* istanbul ignore next */ label => {
    const message = defineMessage({
      id: 'network.audience.age.tooltip-title',
      description: 'Title of the tooltip',
      defaultMessage: 'AGE: {label}',
    });
    return this.props.intl.formatMessage(message, { label });
  };

  ageLabelFormatter = /* istanbul ignore next */ ({ ageRangePercentage }) =>
    formatPercentage(ageRangePercentage, this.props.intl);

  renderLegend = () => (
    <Legend
      content={
        /* istanbul ignore next */ ({ payload }) =>
          payload ? (
            <ul className={chartStyles.legend}>
              {payload.map(item => (
                <li key={item.value} className={chartStyles['legend-item']}>
                  <div className={chartStyles.circle} style={{ backgroundColor: item.color }} />
                  <span>
                    <FormattedMessage
                      id={`network.audience.age.legend.${item.value}`}
                      description={`The label for the legend item '${item.value}'`}
                      defaultMessage={item.value}
                    />
                  </span>
                </li>
              ))}
            </ul>
          ) : null
      }
      wrapperStyle={{
        top: '-46px',
        textTransform: 'uppercase',
        color: colors['grey-40'],
      }}
    />
  );

  renderTooltip = () => (
    <Tooltip
      formatter={this.tooltipFormatter}
      labelFormatter={this.tooltipLabelFormatter}
      isAnimationActive={false}
      offset={40}
      itemStyle={{ color: 'unset', padding: '4px 0' }}
      wrapperStyle={{
        borderRadius: '5px',
        boxShadow: '0 3px 8px 0 rgba(0, 0, 0, 0.2)',
        borderColor: 'white',
      }}
      cursor={false}
      separator=" "
    />
  );

  render() {
    return (
      <div data-qa="age-demographics-chart" style={{ width: '100%', height: '350px' }}>
        <ResponsiveContainer height={350}>
          <BarChart
            data={this.props.ageData}
            margin={{ top: 30, right: 0, left: -20, bottom: 0 }}
            className={classNames(chartStyles.chart, chartStyles['bar-chart'])}
          >
            {this.renderLegend()}
            {this.renderTooltip()}
            <XAxis axisLine={false} dataKey="ageRange" tickLine={false} />
            <YAxis axisLine={false} tickLine={false} tickCount={3} unit="%" />
            <CartesianGrid vertical={false} />
            <Bar
              dataKey="female"
              stackId="age"
              fill="#5a3f5a"
              isAnimationActive={false}
              background={{ fill: 'rgba(241,241,241,.7)' }}
            />
            <Bar dataKey="male" stackId="age" fill="#bca5cf" isAnimationActive={false} />
            <Bar
              dataKey="notSpecified"
              name="not specified"
              stackId="age"
              fill="#ffd3d7"
              isAnimationActive={false}
            />
            <Bar
              dataKey="nonBinary"
              name="non-binary"
              stackId="age"
              fill="#aacad8"
              isAnimationActive={false}
            >
              <LabelList dataKey={this.ageLabelFormatter} position="top" offset={10} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }
}

const AgeDemographicsChart = injectIntl(InternationalizedAgeDemographicsChart);
export { AgeDemographicsChart };
